import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Chart from 'react-apexcharts';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ShoppingBasketOutlinedIcon from '@mui/icons-material/ShoppingBasketOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';

import dayjs from 'dayjs';

import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import { SectionCard, StatCard, EmptyState, currency, number } from './ui';
import { CHART_COLORS, TRUE_COLORS } from '../theme';
import { PAYMENT_LABELS, PaymentCell, brandLabel, itemLabel } from './salePayment';
import { Profit, ProfitSummary } from './Profit';


const Breakdown = ({ rows, empty, valueFormat = currency }: { rows: { label: string; value: number; detail?: string }[]; empty: string; valueFormat?: (v: number) => string }) => {
  const max = Math.max(1, ...rows.map(r => r.value));
  if (rows.length === 0) return <EmptyState compact title={empty} />;
  return (
    <Stack spacing={1.75}>
      {rows.map((r, i) => (
        <Box key={r.label}>
          <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 0.5 }} spacing={1}>
            <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 500 }}>{r.label}</Typography>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, flexShrink: 0 }}>{valueFormat(r.value)}</Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={(r.value / max) * 100}
            sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(20,37,44,0.06)', '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: CHART_COLORS[i % CHART_COLORS.length] } }}
          />
          {r.detail && <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>{r.detail}</Typography>}
        </Box>
      ))}
    </Stack>
  );
};

const DailyReport = () => {
  const axios = useAxios();
  const navigate = useNavigate();
  const { reportDay } = useParams();
  const day = dayjs(reportDay).isValid() ? dayjs(reportDay) : dayjs();

  const [loading, setLoading] = React.useState(true);
  const [receipts, setReceipts] = React.useState<any[]>([]);
  const [products, setProducts] = React.useState<any[]>([]);
  const [orders, setOrders] = React.useState<any[]>([]);
  const [movements, setMovements] = React.useState<any[]>([]);
  const [stockLogs, setStockLogs] = React.useState<any[]>([]);
  const [members, setMembers] = React.useState<any[]>([]);
  // Prepaid balance (Saldo): top-ups are money in that isn't a sale; sales paid with it brought no money that day.
  const [balance, setBalance] = React.useState<{ topups: Record<string, number>; topups_total: number; spent: number } | null>(null);
  // The day's gross profit (administrators only: it shows costs).
  const [profit, setProfit] = React.useState<Profit | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setProfit(null);
    const range = { start_date: day.startOf('day').utc().format(), end_date: day.endOf('day').utc().format() };
    (async () => {
      const [r, p, o, m, s, mem] = await Promise.allSettled([
        axios.get('api/receipts/', { params: range }),
        axios.get('api/receipts/product-summary/', { params: range }),
        axios.get('inventory/orders/', { params: { ...range, paid: true, received: true, cancelled: true } }),
        axios.get('api/movements/', { params: range }),
        axios.get('inventory/inventory-log-history/', { params: { ...range, compact: 1 } }),
        axios.get('management/members/'),
      ]);
      axios.get('api/balance/summary/', { params: range }).then(({ data }) => { if (!cancelled) setBalance(data); }).catch(() => setBalance(null));
      axios.get('api/receipts/profit/', { params: range }).then(({ data }) => { if (!cancelled) setProfit(data); }).catch(() => setProfit(null));
      if (cancelled) return;
      const data = (res: PromiseSettledResult<any>) => (res.status === 'fulfilled' && Array.isArray(res.value.data) ? res.value.data : []);
      setReceipts(data(r).sort((a: any, b: any) => dayjs(a.purchase_date).diff(dayjs(b.purchase_date))));
      setProducts(data(p));
      setOrders(data(o));
      setMovements(data(m));
      setStockLogs(data(s));
      setMembers(data(mem));
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axios, reportDay]);

  const goTo = (d: dayjs.Dayjs) => navigate(`/admin/daily-report/${d.format('YYYY-MM-DD')}`);
  const isToday = day.isSame(dayjs(), 'day');

  const total = receipts.reduce((acc, r) => acc + (Number(r.amount) || 0), 0);
  const itemsSold = receipts.reduce((acc, r) => acc + (r.items ?? []).reduce((a: number, i: any) => a + (Number(i.quantity) || 0), 0), 0);

  const hourly = Array(24).fill(0);
  receipts.forEach(r => { hourly[dayjs(r.purchase_date).hour()] += Number(r.amount) || 0; });
  const firstHour = Math.max(0, Math.min(8, ...receipts.map(r => dayjs(r.purchase_date).hour())));
  const lastHour = Math.min(23, Math.max(20, ...receipts.map(r => dayjs(r.purchase_date).hour())));
  const hours = Array.from({ length: lastHour - firstHour + 1 }, (_, i) => firstHour + i);

  const group = (keyOf: (r: any) => string) => {
    const map = new Map<string, { value: number; count: number }>();
    receipts.forEach(r => {
      const k = keyOf(r);
      const cur = map.get(k) ?? { value: 0, count: 0 };
      map.set(k, { value: cur.value + (Number(r.amount) || 0), count: cur.count + 1 });
    });
    return Array.from(map, ([label, v]) => ({ label, value: v.value, detail: `${number(v.count)} ${v.count === 1 ? 'venta' : 'ventas'}` }))
      .sort((a, b) => b.value - a.value);
  };
  const cashierName = (id: number) => {
    const m = members.find(x => x.member?.id === id);
    return [m?.name, m?.lastname].filter(Boolean).join(' ') || m?.login || m?.member?.username || (id ? `Usuario ${id}` : 'Sin cajero');
  };
  const byPayment = group(r => PAYMENT_LABELS[r.payment] ?? (r.payment || 'Sin registrar'));
  const byCashier = group(r => cashierName(r.cashier));
  const byLocation = group(r => r.checkout?.location?.name ?? 'Sin sucursal');

  const productTotals = new Map<string, number>();
  products.forEach(p => { if (p.product_name) productTotals.set(p.product_name, (productTotals.get(p.product_name) ?? 0) + (Number(p.total_count) || 0)); });
  const topProducts = Array.from(productTotals, ([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value).slice(0, 8);

  // The options chosen most (a café's milks, sizes, extras), each counted as many times as it was sold.
  const optionTotals = new Map<string, number>();
  receipts.forEach(r => (r.items ?? []).forEach((i: any) => (i.modifiers ?? []).forEach((m: any) => {
    const label = m.group_name ? `${m.name} · ${m.group_name}` : m.name;
    optionTotals.set(label, (optionTotals.get(label) ?? 0) + (Number(m.quantity) || 1) * (Number(i.quantity) || 1));
  })));
  const topOptions = Array.from(optionTotals, ([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value).slice(0, 8);
  // Cards charged (on the terminal or online), by brand.
  const cardTotals = new Map<string, { value: number; count: number }>();
  receipts.forEach(r => (r.payments ?? []).forEach((p: any) => {
    if (!p.card_brand) return;
    const label = brandLabel(p.card_brand);
    const current = cardTotals.get(label) ?? { value: 0, count: 0 };
    cardTotals.set(label, { value: current.value + (Number(p.amount) || 0), count: current.count + 1 });
  }));

  const cashIn = movements.filter(m => m.amount > 0).reduce((a, m) => a + m.amount, 0);
  const cashOut = movements.filter(m => m.amount < 0).reduce((a, m) => a - m.amount, 0);
  const paidOrders = orders.filter(o => o.date_paid && dayjs(o.date_paid).isSame(day, 'day')).reduce((a, o) => a + (Number(o.amount) || 0), 0);
  const net = total + cashIn - cashOut - paidOrders + (balance?.topups_total ?? 0) - (balance?.spent ?? 0);

  const hourlyChart = {
    options: {
      chart: { fontFamily: 'Inter, sans-serif', toolbar: { show: false }, zoom: { enabled: false }, animations: { dynamicAnimation: { enabled: false } } },
      colors: [CHART_COLORS[0]],
      plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
      dataLabels: { enabled: false },
      grid: { borderColor: TRUE_COLORS.borderSolid, strokeDashArray: 4 },
      xaxis: { categories: hours.map(h => `${String(h).padStart(2, '0')}:00`), labels: { style: { colors: TRUE_COLORS.secondary }, rotate: 0, hideOverlappingLabels: true }, axisBorder: { show: false }, axisTicks: { show: false } },
      yaxis: { labels: { formatter: (v: number) => currency(v).replace('.00', ''), style: { colors: TRUE_COLORS.secondary } } },
      tooltip: { y: { formatter: (v: number) => currency(v) } },
    },
    series: [{ name: 'Ventas', data: hours.map(h => Math.round(hourly[h] * 100) / 100) }],
  };

  const title = day.format('dddd D [de] MMMM [de] YYYY');

  return (
    <PageShell
      back={{ to: '/admin', label: 'Inicio' }}
      title="Reporte del día"
      subtitle={title.charAt(0).toUpperCase() + title.slice(1)}
      actions={
        <>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', displayPrint: 'none' }}>
            <Tooltip title="Día anterior"><IconButton onClick={() => goTo(day.subtract(1, 'day'))}><ChevronLeftIcon /></IconButton></Tooltip>
            <DatePicker
              value={day}
              maxDate={dayjs()}
              onChange={v => v && v.isValid() && goTo(v)}
              slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
            />
            <Tooltip title="Día siguiente"><span><IconButton onClick={() => goTo(day.add(1, 'day'))} disabled={isToday}><ChevronRightIcon /></IconButton></span></Tooltip>
          </Stack>
          <Button variant="outlined" startIcon={<PrintOutlinedIcon />} onClick={() => window.print()} sx={{ displayPrint: 'none' }}>Imprimir</Button>
        </>
      }
    >
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
      {!loading && receipts.length === 0 && orders.length === 0 && movements.length === 0 && stockLogs.length === 0 ? (
        <SectionCard>
          <EmptyState
            icon={<EventBusyOutlinedIcon />}
            title={isToday ? 'Aún no hay ventas hoy' : 'Sin actividad este día'}
            description={isToday ? 'Las ventas de TRUE POS aparecerán aquí en cuanto se sincronicen.' : 'No se registraron ventas, movimientos ni órdenes.'}
            action={!isToday && <Button onClick={() => goTo(dayjs())}>Ir a hoy</Button>}
          />
        </SectionCard>
      ) : (
        <Grid container spacing={3}>
          {[
            { label: 'Ingresos', value: currency(total), icon: <PaymentsOutlinedIcon /> },
            { label: 'Ventas', value: number(receipts.length), icon: <ReceiptLongOutlinedIcon /> },
            { label: 'Ticket promedio', value: currency(receipts.length ? total / receipts.length : 0), icon: <ShoppingBasketOutlinedIcon /> },
            { label: 'Artículos vendidos', value: number(itemsSold, 2), icon: <Inventory2OutlinedIcon /> },
          ].map(card => (
            <Grid key={card.label} size={{ xs: 6, sm: 6, lg: 3 }}>
              <StatCard label={card.label} value={card.value} icon={card.icon} loading={loading} />
            </Grid>
          ))}

          <Grid size={{ xs: 12, lg: 8 }}>
            <SectionCard title="Ventas por hora">
              {receipts.length ? <Chart type="bar" height={280} options={hourlyChart.options} series={hourlyChart.series} /> : <EmptyState compact title="Sin ventas este día" />}
            </SectionCard>
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <SectionCard title="Corte de caja" subtitle="Flujo del día">
              <Stack spacing={1.25}>
                {[
                  { label: 'Ventas', value: total, sign: 1 },
                  { label: 'Entradas de efectivo', value: cashIn, sign: 1 },
                  { label: 'Salidas de efectivo', value: cashOut, sign: -1 },
                  { label: 'Órdenes pagadas', value: paidOrders, sign: -1 },
                  ...(balance?.topups_total ? [{ label: 'Recargas de saldo', value: balance.topups_total, sign: 1 }] : []),
                  ...(balance?.spent ? [{ label: 'Pagado con saldo (ya cobrado antes)', value: balance.spent, sign: -1 }] : []),
                ].map(row => (
                  <Stack key={row.label} direction="row" sx={{ justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{row.label}</Typography>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: row.sign < 0 && row.value > 0 ? TRUE_COLORS.danger : 'text.primary' }}>
                      {row.sign < 0 && row.value > 0 ? '−' : ''}{currency(row.value)}
                    </Typography>
                  </Stack>
                ))}
                <Box sx={{ borderTop: `1px solid ${TRUE_COLORS.borderSolid}`, pt: 1.5, mt: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontWeight: 700 }}>Neto</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.125rem', color: net < 0 ? TRUE_COLORS.danger : TRUE_COLORS.success }}>{currency(net)}</Typography>
                </Box>
                <Box sx={{ pt: 1 }}>
                  <Typography variant="overline" sx={{ color: 'text.secondary' }}>Por método de pago</Typography>
                  <Box sx={{ mt: 1 }}><Breakdown rows={byPayment} empty="Sin pagos" /></Box>
                  {cardTotals.size > 0 && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }} data-testid="card-brands">
                      Tarjetas: {Array.from(cardTotals, ([label, v]) => `${label} ${currency(v.value)} (${v.count})`).join(' · ')}
                    </Typography>
                  )}
                  {!!balance?.topups_total && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }} data-testid="balance-topups">
                      Recargas de saldo: {Object.entries(balance.topups).map(([m, v]) => `${currency(v)} ${({ cash: 'en efectivo', card: 'con tarjeta', online: 'en línea' } as Record<string, string>)[m] ?? m}`).join(' · ')}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </SectionCard>
          </Grid>

          {profit && receipts.length > 0 && (
            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <SectionCard title="Utilidad" subtitle="Lo que ganaste con lo que vendiste">
                <ProfitSummary profit={profit} />
              </SectionCard>
            </Grid>
          )}
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <SectionCard title="Productos más vendidos" subtitle="Unidades">
              <Breakdown rows={topProducts} empty="Sin productos vendidos" valueFormat={v => number(v, 2)} />
            </SectionCard>
          </Grid>
          {topOptions.length > 0 && (
            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <SectionCard title="Opciones más pedidas" subtitle="Leches, tamaños, extras…">
                <Breakdown rows={topOptions} empty="Sin opciones" valueFormat={v => number(v, 2)} />
              </SectionCard>
            </Grid>
          )}
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <SectionCard title="Por cajero">
              <Breakdown rows={byCashier} empty="Sin ventas" />
            </SectionCard>
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <SectionCard title="Por sucursal">
              <Breakdown rows={byLocation} empty="Sin ventas" />
            </SectionCard>
          </Grid>

          <Grid size={{ xs: 12, lg: stockLogs.length ? 8 : 12 }}>
            <SectionCard title="Ventas del día" subtitle={`${number(receipts.length)} recibos`} noPadding>
              {receipts.length === 0 ? <EmptyState compact title="Sin ventas" /> : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Hora</TableCell>
                        <TableCell>Sucursal</TableCell>
                        <TableCell>Cajero</TableCell>
                        <TableCell>Artículos</TableCell>
                        <TableCell>Pago</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {receipts.map(r => (
                        <TableRow key={r.id} hover>
                          <TableCell>{dayjs(r.purchase_date).format('HH:mm')}</TableCell>
                          <TableCell>{r.checkout?.location?.name ?? '—'}</TableCell>
                          <TableCell>{cashierName(r.cashier)}</TableCell>
                          <TableCell sx={{ maxWidth: 340 }}>
                            {(r.items ?? []).length === 0 ? '—' : (r.items ?? []).map((i: any, n: number) => (
                              <Typography key={n} sx={{ fontSize: '0.8125rem', lineHeight: 1.5 }}>{number(i.quantity, 2)}× {itemLabel(i)}</Typography>
                            ))}
                          </TableCell>
                          <TableCell><PaymentCell sale={r} /></TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{currency(r.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </SectionCard>
          </Grid>
          {stockLogs.length > 0 && (
            <Grid size={{ xs: 12, lg: 4 }}>
              <SectionCard title="Movimientos de inventario" noPadding>
                <Table size="small">
                  <TableBody>
                    {stockLogs.slice(0, 30).map(l => (
                      <TableRow key={l.id}>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>{l.inventory_item?.product?.name}</Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{l.reason || 'Ajuste'} · {l.inventory_item?.location?.name}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: l.change > 0 ? TRUE_COLORS.success : TRUE_COLORS.danger }}>
                          {l.change > 0 ? '+' : ''}{number(l.change, 2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </SectionCard>
            </Grid>
          )}
        </Grid>
      )}
    </PageShell>
  );
};

export default DailyReport;
